import { useEffect, useMemo, useState } from "react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import ContactSearchCombobox from "@/components/Crm/ContactSearchCombobox";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";
import { iniciais, isoLocalDate, primeiroNome, tomDoAvatar } from "@/components/Agenda/agenda-utils";
import {
  FUSOS_SUGERIDOS,
  FUSO_PADRAO,
  horaNoFuso,
  inputLocalParaUtc,
  mesmaHoraDeParede,
  offsetLegivel,
  utcParaInputLocal,
} from "@shared/timezone";
import { AlertTriangle, Building2, Check, ChevronDown, Globe, Plus, Users, X } from "lucide-react";

/** As quatro que cobrem quase tudo ficam em chip; o resto, no seletor. */
const DURACOES_RAPIDAS = [30, 45, 60];
const DURACOES = [15, 30, 45, 60, 90, 120];

const ATALHOS_BRIEFING = ["Dor levantada", "Orçamento", "Quem decide", "Objeções", "Prazo de decisão"];

/**
 * As duas cidades do dia a dia da operação, em chip. O resto do Brasil e do
 * mundo fica no seletor ao lado.
 */
const FUSOS_RAPIDOS = [
  { tz: "America/Sao_Paulo", label: "Brasília" },
  { tz: "America/Cuiaba", label: "Cuiabá" },
];

/** Fuso do relógio de quem está preenchendo — para o aviso de diferença. */
const fusoDoNavegador =
  Intl.DateTimeFormat().resolvedOptions().timeZone || FUSO_PADRAO;

type ContatoEscolhido = { id: number; name: string | null } | null;

/**
 * Marcar ou editar uma reunião.
 *
 * O caso que desenhou esta tela: a SDR marca para a closer. Por isso o
 * organizador é escolhível (não é sempre quem está logado), a lista de
 * participantes é explícita, e os horários livres aparecem antes do salvar —
 * sem eles a SDR teria que adivinhar quando a closer está livre.
 *
 * O formulário é dividido em Detalhes (o que a reunião é e quando) e Briefing
 * (o que o closer precisa saber). Quem marca rápido nunca abre a segunda aba;
 * quem passa o bastão preenche as duas.
 */
export default function MeetingSheet({
  open,
  onClose,
  meetingId,
  defaults,
}: {
  open: boolean;
  onClose: () => void;
  /** Quando presente, edita em vez de criar. */
  meetingId?: number | null;
  /** Pré-preenchimento vindo do card do contato ou da negociação. */
  defaults?: {
    contactId?: number | null;
    contactName?: string | null;
    dealId?: number | null;
    startAt?: Date;
    attendeeUserIds?: number[];
  };
}) {
  const { user } = useAuth();
  const utils = trpc.useUtils();
  const meuId = (user as any)?.id as number | undefined;
  const isEdit = meetingId != null;

  const [aba, setAba] = useState("detalhes");
  const [title, setTitle] = useState("");
  const [briefing, setBriefing] = useState("");
  const [contato, setContato] = useState<ContatoEscolhido>(null);
  const [dealId, setDealId] = useState<string>("none");
  const [organizerUserId, setOrganizerUserId] = useState<string>("");
  const [attendees, setAttendees] = useState<number[]>([]);
  const [pessoasAberto, setPessoasAberto] = useState(false);
  const [dia, setDia] = useState<string>("");
  const [hora, setHora] = useState<string>("09:00");
  const [duracao, setDuracao] = useState<number>(45);
  const [locationType, setLocationType] = useState<string>("online");
  const [locationText, setLocationText] = useState("");
  const [meetingUrl, setMeetingUrl] = useState("");
  const [espelhar, setEspelhar] = useState(true);
  // Fuso EM QUE a reunião é marcada. A hora digitada abaixo é lida nele, não
  // no relógio de quem está preenchendo — marcar 14:00 para um cliente de
  // Manaus tem que virar 14:00 em Manaus.
  const [fuso, setFuso] = useState(FUSO_PADRAO);

  const usersQuery = trpc.agenda.meetings.listUsers.useQuery(undefined, { enabled: open });
  const equipe = usersQuery.data ?? [];
  const fusoDoWorkspace = trpc.agenda.meetings.timezone.useQuery(undefined, { enabled: open });
  const meetingQuery = trpc.agenda.meetings.get.useQuery(
    { id: meetingId ?? 0 },
    { enabled: open && isEdit },
  );
  // Negociações do contato escolhido — vincular a reunião ao deal é o que faz
  // ela aparecer na timeline da oportunidade.
  const dealsQuery = trpc.crm.deals.listByContact.useQuery(
    { contactId: contato?.id ?? 0 },
    { enabled: open && !!contato?.id },
  );

  // ── Carga inicial ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    setAba("detalhes");
    setPessoasAberto(false);
    if (isEdit) {
      const m = meetingQuery.data;
      if (!m) return;
      setTitle(m.meeting.title);
      setBriefing(m.meeting.briefing ?? "");
      setContato(m.contact?.id ? { id: m.contact.id, name: m.contact.name } : null);
      setDealId(m.meeting.dealId ? String(m.meeting.dealId) : "none");
      setOrganizerUserId(m.meeting.organizerUserId ? String(m.meeting.organizerUserId) : "");
      setAttendees(m.attendees.map((a: any) => a.attendee.userId));
      setFuso(m.meeting.timezone);
      const [d, h] = utcParaInputLocal(new Date(m.meeting.startAt), m.meeting.timezone).split("T");
      setDia(d);
      setHora(h);
      setDuracao(
        Math.round(
          (new Date(m.meeting.endAt).getTime() - new Date(m.meeting.startAt).getTime()) / 60_000,
        ),
      );
      setLocationType(m.meeting.locationType);
      setLocationText(m.meeting.locationText ?? "");
      setMeetingUrl(m.meeting.meetingUrl ?? "");
      setEspelhar(m.meeting.mirrorLinkedAgendas);
      return;
    }
    // Criação: próxima hora cheia é um chute melhor que "agora".
    const tz = fusoDoWorkspace.data ?? FUSO_PADRAO;
    setFuso(tz);
    const base = defaults?.startAt ?? proximaHoraCheia();
    const [d, h] = utcParaInputLocal(base, tz).split("T");
    setTitle(defaults?.contactName ? `Reunião com ${defaults.contactName}` : "");
    setBriefing("");
    setContato(
      defaults?.contactId ? { id: defaults.contactId, name: defaults.contactName ?? null } : null,
    );
    setDealId(defaults?.dealId ? String(defaults.dealId) : "none");
    setOrganizerUserId(meuId ? String(meuId) : "");
    setAttendees(defaults?.attendeeUserIds ?? []);
    setDia(d);
    setHora(h);
    setDuracao(45);
    setLocationType("online");
    setLocationText("");
    setMeetingUrl("");
    setEspelhar(true);
    // `defaults` é objeto novo a cada render do pai; depender dele reabriria o
    // formulário em loop. As chaves que importam estão listadas uma a uma.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    isEdit,
    meetingQuery.data,
    meuId,
    defaults?.contactId,
    defaults?.dealId,
    defaults?.startAt?.getTime(),
    fusoDoWorkspace.data,
  ]);

  const participantes = useMemo(() => {
    const org = organizerUserId ? Number(organizerUserId) : null;
    return [...new Set([...(org ? [org] : []), ...attendees])];
  }, [organizerUserId, attendees]);

  // A hora digitada vale NO FUSO ESCOLHIDO. Ler com `new Date(valor)` a
  // interpretaria no relógio do navegador, e "14:00 em Manaus" viraria 14:00
  // de Brasília — uma hora de diferença, silenciosa.
  const inicio = dia && hora ? inputLocalParaUtc(`${dia}T${hora}`, fuso) : null;
  const fim = inicio ? new Date(inicio.getTime() + duracao * 60_000) : null;

  // ── Conflitos, avisados enquanto o usuário ainda pode mudar o horário ─────
  const conflitosQuery = trpc.agenda.meetings.checkConflicts.useQuery(
    {
      userIds: participantes,
      startAt: inicio ?? new Date(),
      endAt: fim ?? new Date(),
      excludeMeetingId: meetingId ?? undefined,
    },
    { enabled: open && participantes.length > 0 && !!inicio && !!fim },
  );
  const conflitos = conflitosQuery.data;
  const foraDoExpediente = conflitos?.foraDoExpediente ?? [];
  const temConflito =
    (conflitos?.meetings.length ?? 0) > 0 || (conflitos?.blocks.length ?? 0) > 0 || foraDoExpediente.length > 0;
  // Bloqueio manual, espelho e fora do expediente o servidor recusa sem
  // apelação; reunião e compromisso do Google ele aceita forçar. A cor do
  // aviso segue essa diferença: vermelho é parede, âmbar é "confirme".
  const conflitoDuro =
    foraDoExpediente.length > 0 || (conflitos?.blocks ?? []).some((b: any) => b.externalProvider == null);

  // ── Horários livres ───────────────────────────────────────────────────────
  // Sempre carregados quando há participante e dia: o desenho é "escolha um
  // horário livre", não "descubra depois que deu conflito".
  const sugestoesQuery = trpc.agenda.meetings.freeSlots.useQuery(
    {
      userIds: participantes,
      date: dia || isoLocalDate(new Date()),
      durationMinutes: duracao,
    },
    { enabled: open && participantes.length > 0 && !!dia },
  );
  const livres = sugestoesQuery.data?.slots ?? [];
  /** Primeiro livre a partir do horário atual — o botão "mover para". */
  const sugestao = useMemo(() => {
    if (!inicio || livres.length === 0) return null;
    const depois = livres.map((s: any) => new Date(s)).filter((d: Date) => d > inicio);
    return (depois[0] ?? new Date(livres[0])) as Date;
  }, [livres, inicio]);

  // ── Agendas em outras empresas ────────────────────────────────────────────
  // Quem atua em duas workspaces tem duas agendas que não se enxergam. A
  // consulta diz quais serão ocupadas, para a opção abaixo ser uma escolha
  // informada em vez de um efeito colateral invisível.
  const espelhosQuery = trpc.agenda.meetings.linkedAgendasPreview.useQuery(
    { userIds: participantes },
    { enabled: open && participantes.length > 0 },
  );
  const espelhos = espelhosQuery.data ?? [];

  const invalidate = () => {
    utils.agenda.meetings.list.invalidate();
    if (meetingId) utils.agenda.meetings.get.invalidate({ id: meetingId });
    if (dealId !== "none") {
      utils.crm.deals.listActivities.invalidate({ dealId: Number(dealId) });
    }
  };

  const create = trpc.agenda.meetings.create.useMutation();
  const update = trpc.agenda.meetings.update.useMutation();
  const salvando = create.isPending || update.isPending;

  async function salvar(force = false) {
    if (!title.trim()) return toast.error("Dê um título à reunião");
    if (!inicio || !fim) return toast.error("Escolha data e hora");
    if (!organizerUserId) return toast.error("Escolha quem organiza");

    const payload = {
      title: title.trim(),
      briefing: briefing.trim() || null,
      contactId: contato?.id ?? null,
      dealId: dealId !== "none" ? Number(dealId) : null,
      organizerUserId: Number(organizerUserId),
      attendeeUserIds: attendees,
      startAt: inicio,
      endAt: fim,
      timezone: fuso,
      locationType: locationType as "online" | "presencial" | "telefone",
      locationText: locationText.trim() || null,
      meetingUrl: meetingUrl.trim() || null,
      mirrorLinkedAgendas: espelhar,
      force,
    };

    try {
      if (isEdit) await update.mutateAsync({ id: meetingId!, ...payload });
      else await create.mutateAsync(payload);
      toast.success(isEdit ? "Reunião atualizada" : "Reunião marcada");
      invalidate();
      onClose();
    } catch (e: any) {
      // O servidor barra conflito com CONFLICT em vez de gravar calado. Aqui a
      // decisão volta para quem está marcando: às vezes sobrepor é intencional.
      // BAD_REQUEST (bloqueio, fora do expediente) não oferece força nenhuma.
      if (e?.data?.code === "CONFLICT" && !force) {
        toast.error(e.message, {
          duration: 12000,
          action: { label: "Marcar assim mesmo", onClick: () => salvar(true) },
        });
        return;
      }
      toast.error(toUserFriendlyError(e, "Erro ao salvar a reunião"));
    }
  }

  const escolhidos = participantes
    .map((id) => equipe.find((u: any) => u.id === id))
    .filter(Boolean) as any[];
  const organizador = equipe.find((u: any) => String(u.id) === organizerUserId);
  const outros = escolhidos.length - 1;
  const resumoPessoas =
    escolhidos.length === 0
      ? "Escolha quem participa"
      : outros <= 0
        ? `Só ${primeiroNome(organizador?.name, organizador?.email)} (organiza)`
        : outros === 1
          ? `${primeiroNome(organizador?.name, organizador?.email)} + ${primeiroNome(
              escolhidos.find((u) => String(u.id) !== organizerUserId)?.name,
              escolhidos.find((u) => String(u.id) !== organizerUserId)?.email,
            )}`
          : `${primeiroNome(organizador?.name, organizador?.email)} + ${outros}`;

  const rodapeResumo =
    inicio && fim
      ? `${new Date(`${dia}T12:00:00`).toLocaleDateString("pt-BR", { weekday: "long", day: "numeric", month: "short" })} · ${horaNoFuso(inicio, fuso)} – ${horaNoFuso(fim, fuso)} · ${
          locationType === "online" ? "Online" : locationType === "telefone" ? "Telefone" : "Presencial"
        } · ${escolhidos.length} da equipe`
      : "";

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-2xl p-0 gap-0 flex flex-col max-h-[88vh] bg-white dark:bg-card">
        <DialogHeader className="px-5 pt-5 pb-3 text-left">
          <DialogTitle>{isEdit ? "Editar reunião" : "Nova reunião"}</DialogTitle>
          <DialogDescription>
            Marque na sua agenda ou na de um colega — quem participa vê na própria agenda.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={aba} onValueChange={setAba} className="flex-1 flex flex-col min-h-0">
          <TabsList className="mx-5 self-start">
            <TabsTrigger value="detalhes" className="gap-1.5">
              Detalhes
              <span className="text-[10px] rounded-full bg-primary/10 text-primary px-1.5 py-px">essencial</span>
            </TabsTrigger>
            <TabsTrigger value="briefing" className="gap-1.5">
              Briefing
              <span className="text-[10px] rounded-full bg-muted text-muted-foreground px-1.5 py-px">opcional</span>
            </TabsTrigger>
          </TabsList>

          {/* ── Detalhes ──────────────────────────────────────────────── */}
          <TabsContent value="detalhes" className="px-5 pb-4 space-y-4 mt-3 overflow-y-auto data-[state=active]:flex-1">
            <div>
              <Label className="flex items-center gap-1.5">
                Título
                <span className="text-[10px] uppercase tracking-wide rounded bg-primary/10 text-primary px-1.5 py-px">
                  obrigatório
                </span>
              </Label>
              <Input
                className="mt-1"
                placeholder="Ex: Apresentação da proposta"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>

            <div className="grid sm:grid-cols-3 gap-3">
              <div className="min-w-0">
                <Label>Contato</Label>
                <div className="mt-1">
                  <ContactSearchCombobox
                    value={contato as any}
                    onChange={(c: any) => {
                      setContato({ id: c.id, name: c.name });
                      setDealId("none");
                      if (!title.trim()) setTitle(`Reunião com ${c.name ?? "contato"}`);
                    }}
                  />
                </div>
              </div>
              <div className="min-w-0">
                <Label>Oportunidade</Label>
                {contato?.id && (dealsQuery.data ?? []).length > 0 ? (
                  <Select value={dealId} onValueChange={setDealId}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nenhuma</SelectItem>
                      {(dealsQuery.data ?? []).map((row: any) => {
                        // `deals.listByContact` devolve o deal PLANO; outras
                        // rotas do CRM devolvem { deal, … }. Aceita os dois.
                        const d = row.deal ?? row;
                        return (
                          <SelectItem key={d.id} value={String(d.id)}>
                            {d.title}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="mt-1 h-9 flex items-center text-xs text-muted-foreground">
                    {contato?.id ? "Sem oportunidade aberta." : "Escolha um contato."}
                  </p>
                )}
              </div>

              {/* ── Quem participa ───────────────────────────────────── */}
              <div className="min-w-0">
              <Label className="flex items-center gap-1.5">
                <Users className="h-3.5 w-3.5" />
                Quem participa
              </Label>
              <Popover open={pessoasAberto} onOpenChange={setPessoasAberto}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="mt-1 w-full h-9 flex items-center gap-2 rounded-md border border-input bg-background px-2.5 text-sm hover:bg-accent/50 transition-colors"
                    title="Toque para incluir ou tirar gente da reunião"
                  >
                    <span className="flex -space-x-2 shrink-0">
                      {escolhidos.slice(0, 3).map((u) => (
                        <span
                          key={u.id}
                          className={`h-6 w-6 rounded-full ring-2 ring-background inline-flex items-center justify-center text-[10px] font-semibold ${tomDoAvatar(u.id)}`}
                        >
                          {iniciais(u.name, u.email)}
                        </span>
                      ))}
                      {escolhidos.length === 0 && (
                        <span className="h-6 w-6 rounded-full border border-dashed border-muted-foreground/40 inline-flex items-center justify-center">
                          <Plus className="h-3 w-3 text-muted-foreground" />
                        </span>
                      )}
                    </span>
                    <span className="flex-1 text-left truncate text-xs">{resumoPessoas}</span>
                    <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-[22rem] p-1.5 max-h-72 overflow-y-auto">
                  {equipe.map((u: any) => {
                    const organiza = String(u.id) === organizerUserId;
                    const dentro = organiza || attendees.includes(u.id);
                    return (
                      <div
                        key={u.id}
                        className={`flex items-center gap-2 rounded-md px-2 py-1.5 ${dentro ? "bg-primary/5" : ""}`}
                      >
                        <span className={`h-7 w-7 rounded-full inline-flex items-center justify-center text-[11px] font-semibold shrink-0 ${tomDoAvatar(u.id)}`}>
                          {iniciais(u.name, u.email)}
                        </span>
                        <button
                          type="button"
                          className="min-w-0 flex-1 text-left"
                          onClick={() =>
                            setAttendees((prev) =>
                              organiza
                                ? prev
                                : prev.includes(u.id)
                                  ? prev.filter((x) => x !== u.id)
                                  : [...prev, u.id],
                            )
                          }
                        >
                          <span className="block text-sm truncate">{u.name ?? u.email}</span>
                          <span className="block text-xs text-muted-foreground truncate">
                            {organiza ? "organiza" : u.jobTitle || "—"}
                          </span>
                        </button>
                        {organiza ? (
                          <span className="text-[10px] text-primary font-medium shrink-0">organiza</span>
                        ) : (
                          <>
                            {/* Trocar o organizador sem sair do popover: é a SDR
                                marcando para a closer, o caso que criou a tela. */}
                            <button
                              type="button"
                              className="text-[10px] text-muted-foreground hover:text-foreground shrink-0"
                              onClick={() => {
                                setAttendees((prev) => [
                                  ...prev.filter((x) => x !== u.id),
                                  ...(organizerUserId ? [Number(organizerUserId)] : []),
                                ]);
                                setOrganizerUserId(String(u.id));
                              }}
                            >
                              organiza
                            </button>
                            {dentro && <Check className="h-4 w-4 text-primary shrink-0" />}
                          </>
                        )}
                      </div>
                    );
                  })}
                </PopoverContent>
              </Popover>
              </div>
            </div>

            {/* ── Quando ─────────────────────────────────────────────── */}
            <div className="rounded-lg border border-border p-3 space-y-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Quando</Label>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <Globe className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">Horário do cliente em</span>
                  {FUSOS_RAPIDOS.map((f) => (
                    <button
                      key={f.tz}
                      type="button"
                      onClick={() => setFuso(f.tz)}
                      className={`text-xs rounded-full border px-2 py-0.5 transition-colors ${
                        fuso === f.tz
                          ? "border-primary bg-primary/10 text-primary font-medium"
                          : "border-border text-muted-foreground hover:border-primary/50 hover:text-foreground"
                      }`}
                    >
                      {f.label}
                    </button>
                  ))}
                  <Select value={fuso} onValueChange={setFuso}>
                    {/* Mesma altura dos chips; sem seta dupla nem largura mínima
                        do trigger padrão, que destoava ao lado deles. */}
                    <SelectTrigger
                      /* `data-[size=…]:h-9` do componente base ganha de uma
                         classe de altura solta — a sobrescrita tem que vir
                         pelo mesmo seletor. */
                      className={`data-[size=default]:h-6 w-auto gap-1 rounded-full border px-2 py-0 text-xs shadow-none [&>svg]:size-3 [&>svg]:opacity-60 ${
                        FUSOS_RAPIDOS.some((f) => f.tz === fuso)
                          ? "[&>span]:hidden border-border text-muted-foreground hover:border-primary/50 hover:text-foreground"
                          : "border-primary bg-primary/10 text-primary font-medium"
                      }`}
                      aria-label="Outro fuso"
                      title="Outro fuso"
                    >
                      {FUSOS_RAPIDOS.some((f) => f.tz === fuso) ? <span>outro</span> : <SelectValue />}
                    </SelectTrigger>
                    <SelectContent>
                      {/* Um fuso gravado fora da lista curada (importação, versão
                          antiga) continua selecionável — some da lista seria
                          perder o valor ao salvar qualquer outro campo. */}
                      {!FUSOS_SUGERIDOS.some((f) => f.tz === fuso) && (
                        <SelectItem value={fuso}>{fuso}</SelectItem>
                      )}
                      {FUSOS_SUGERIDOS.map((f) => (
                        <SelectItem key={f.tz} value={f.tz}>
                          {f.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex flex-wrap items-end gap-3">
                <div className="shrink-0">
                  <Label className="text-xs text-muted-foreground">Dia</Label>
                  <Input
                    type="date"
                    className="mt-1 w-[10.5rem]"
                    value={dia}
                    onChange={(e) => setDia(e.target.value)}
                  />
                </div>
                <div className="shrink-0">
                  <Label className="text-xs text-muted-foreground">Início</Label>
                  <Input
                    type="time"
                    className="mt-1 w-[6.5rem]"
                    value={hora}
                    onChange={(e) => setHora(e.target.value)}
                  />
                </div>
                <div className="min-w-0">
                  <Label className="text-xs text-muted-foreground">Duração</Label>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {DURACOES_RAPIDAS.map((d) => (
                      <button
                        key={d}
                        type="button"
                        onClick={() => setDuracao(d)}
                        className={`text-xs rounded-md border px-2 h-9 transition-colors ${
                          duracao === d
                            ? "border-primary bg-primary/10 text-primary font-medium"
                            : "border-input text-muted-foreground hover:text-foreground"
                        }`}
                      >
                        {d}min
                      </button>
                    ))}
                    {!DURACOES_RAPIDAS.includes(duracao) && (
                      <span className="text-xs rounded-md border border-primary bg-primary/10 text-primary font-medium px-2 h-9 inline-flex items-center">
                        {duracao}min
                      </span>
                    )}
                    <Select value={String(duracao)} onValueChange={(v) => setDuracao(Number(v))}>
                      <SelectTrigger className="h-9 w-9 justify-center p-0 [&>svg:last-child]:hidden" aria-label="Outra duração">
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                      </SelectTrigger>
                      <SelectContent>
                        {DURACOES.map((d) => (
                          <SelectItem key={d} value={String(d)}>
                            {d} minutos
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {inicio && fim && (
                <p className="text-xs">
                  <span className="font-medium">
                    {horaNoFuso(inicio, fuso)} – {horaNoFuso(fim, fuso)} em{" "}
                    {FUSOS_RAPIDOS.find((f) => f.tz === fuso)?.label ?? offsetLegivel(fim, fuso)}
                  </span>
                  <span className="text-muted-foreground">
                  {/* Sem isto, quem marca de outro estado não descobre que a
                      reunião cai numa hora diferente da que digitou. */}
                  {!mesmaHoraDeParede(inicio, fuso, fusoDoNavegador) ? (
                    <>
                      {" · no seu relógio: "}
                      <strong>
                        {horaNoFuso(inicio, fusoDoNavegador)} – {horaNoFuso(fim, fusoDoNavegador)}
                      </strong>
                    </>
                  ) : (
                    " · mesmo horário que o seu"
                  )}
                  </span>
                </p>
              )}

              {participantes.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-xs text-muted-foreground">Livre neste dia:</span>
                  {sugestoesQuery.isLoading ? (
                    <span className="text-xs text-muted-foreground">calculando…</span>
                  ) : livres.length === 0 ? (
                    <span className="text-xs text-muted-foreground">
                      nenhum horário livre para todos — confira a disponibilidade de cada um.
                    </span>
                  ) : (
                    livres.slice(0, 10).map((s: any) => {
                      const d = new Date(s);
                      const atual = inicio?.getTime() === d.getTime();
                      return (
                        <button
                          key={d.toISOString()}
                          type="button"
                          onClick={() => {
                            const [nd, nh] = utcParaInputLocal(d, fuso).split("T");
                            setDia(nd);
                            setHora(nh);
                          }}
                          className={`text-xs rounded-full border px-2 py-0.5 transition-colors ${
                            atual
                              ? "border-emerald-500 bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300 font-medium"
                              : "border-emerald-200 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-900 dark:text-emerald-400 dark:hover:bg-emerald-950/50"
                          }`}
                        >
                          {horaNoFuso(d, fuso)}
                        </button>
                      );
                    })
                  )}
                </div>
              )}

              {temConflito && (
                <div
                  className={`rounded-md border p-2.5 space-y-1 ${
                    conflitoDuro
                      ? "border-rose-300 bg-rose-50 dark:border-rose-900 dark:bg-rose-950/40"
                      : "border-amber-300 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/40"
                  }`}
                >
                  <p
                    className={`text-xs font-medium flex items-center gap-1.5 ${
                      conflitoDuro ? "text-rose-900 dark:text-rose-200" : "text-amber-900 dark:text-amber-200"
                    }`}
                  >
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                    {foraDoExpediente.length > 0
                      ? "Fora do horário de trabalho"
                      : conflitoDuro
                        ? "Horário bloqueado"
                        : "Conflito de agenda"}
                  </p>
                  {foraDoExpediente.map((p: any) => (
                    <p key={`e${p.userId}`} className="text-xs text-muted-foreground">
                      {p.descricao}.
                    </p>
                  ))}
                  {conflitos?.meetings.map((m: any) => (
                    <p key={`m${m.meetingId}-${m.userId}`} className="text-xs text-muted-foreground">
                      Choca com “{m.title}” ({horaNoFuso(new Date(m.startAt), fuso)}) na agenda de{" "}
                      {m.userName ?? "um participante"}.
                    </p>
                  ))}
                  {conflitos?.blocks.map((b: any) => (
                    <p key={`b${b.blockId}-${b.userId}`} className="text-xs text-muted-foreground">
                      {b.userName ?? "Participante"}{" "}
                      {b.externalProvider === "google" ? "tem no Google Agenda" : "bloqueou a agenda"} às{" "}
                      {horaNoFuso(new Date(b.startAt), fuso)}
                      {b.reason ? ` (${b.reason})` : ""}.
                    </p>
                  ))}
                  {sugestao && (
                    <div className="pt-0.5">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs bg-background"
                        onClick={() => {
                          const [nd, nh] = utcParaInputLocal(sugestao, fuso).split("T");
                          setDia(nd);
                          setHora(nh);
                        }}
                      >
                        Mover para {horaNoFuso(sugestao, fuso)}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ── Formato ────────────────────────────────────────────── */}
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Formato</Label>
                <div className="mt-1 inline-flex rounded-md border border-input p-0.5 bg-muted/40">
                  {[
                    { v: "online", label: "Online" },
                    { v: "presencial", label: "Presencial" },
                    // Reunião antiga gravada como "telefone" continua editável:
                    // some da escolha, mas não vira "online" pelas costas.
                    ...(locationType === "telefone" ? [{ v: "telefone", label: "Telefone" }] : []),
                  ].map((f) => (
                    <button
                      key={f.v}
                      type="button"
                      onClick={() => setLocationType(f.v)}
                      className={`text-xs px-2.5 h-7 rounded transition-colors ${
                        locationType === f.v
                          ? "bg-background font-semibold shadow-sm"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label>
                  {locationType === "online" ? "Link da chamada" : locationType === "telefone" ? "Telefone" : "Endereço"}
                </Label>
                {locationType === "online" ? (
                  <Input
                    className="mt-1"
                    placeholder="https://meet…"
                    value={meetingUrl}
                    onChange={(e) => setMeetingUrl(e.target.value)}
                  />
                ) : (
                  <Input
                    className="mt-1"
                    placeholder={locationType === "telefone" ? "(61) 9…" : "Rua, número, sala"}
                    value={locationText}
                    onChange={(e) => setLocationText(e.target.value)}
                  />
                )}
              </div>
            </div>

            {espelhos.length > 0 && (
              <div className="rounded-lg border border-border p-3">
                <label className="flex items-start gap-2 cursor-pointer">
                  <Checkbox
                    checked={espelhar}
                    onCheckedChange={(v) => setEspelhar(v === true)}
                    className="mt-0.5"
                  />
                  <span className="text-sm">
                    <span className="flex items-center gap-1.5 font-medium">
                      <Building2 className="h-3.5 w-3.5" />
                      Bloquear a agenda nas outras empresas
                    </span>
                    <span className="block text-xs text-muted-foreground mt-1">
                      Estas pessoas atuam em mais de uma empresa. Marcado, o horário fica ocupado lá
                      também — sem título, contato nem negociação, só o nome desta empresa.
                    </span>
                    <span className="mt-1.5 block space-y-0.5">
                      {espelhos.map((e: any) => {
                        const pessoa = equipe.find((u: any) => u.id === e.origemUserId);
                        return (
                          <span
                            key={`${e.origemUserId}-${e.userId}`}
                            className="block text-xs text-muted-foreground"
                          >
                            • {pessoa?.name ?? pessoa?.email ?? "Participante"} na{" "}
                            <strong>{e.tenantName ?? "outra empresa"}</strong>
                          </span>
                        );
                      })}
                    </span>
                  </span>
                </label>
              </div>
            )}
          </TabsContent>

          {/* ── Briefing ──────────────────────────────────────────────── */}
          <TabsContent value="briefing" className="px-5 pb-4 mt-3 flex flex-col min-h-0 data-[state=active]:flex-1">
            <p className="text-xs text-muted-foreground">
              O que o closer precisa saber antes da call. Fica no card da reunião e na timeline da
              oportunidade.
            </p>
            <Textarea
              /* O Textarea base traz `field-sizing-content` e `min-h-16`: vazio,
                 ele encolhe para duas linhas e ignora `rows` e qualquer classe
                 de altura. Inline vence os dois — e a caixa precisa nascer
                 grande, é o convite para escrever o briefing. */
              style={{ fieldSizing: "fixed", minHeight: "20rem" } as any}
              className="mt-3 flex-1 resize-y"
              rows={16}
              placeholder="Dor levantada, orçamento, quem decide, objeções que apareceram…"
              value={briefing}
              onChange={(e) => setBriefing(e.target.value)}
            />
            <div className="mt-3 flex flex-wrap gap-1.5">
              {ATALHOS_BRIEFING.map((a) => (
                <button
                  key={a}
                  type="button"
                  onClick={() =>
                    setBriefing((b) => (b.trim() ? `${b.replace(/\s+$/, "")}\n${a}: ` : `${a}: `))
                  }
                  className="text-[11px] rounded-full border border-border px-2 py-0.5 text-muted-foreground hover:border-primary/50 hover:text-foreground transition-colors"
                >
                  + {a}
                </button>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        {/* ── Rodapé ─────────────────────────────────────────────────── */}
        <div className="border-t bg-white dark:bg-card px-5 py-3 flex items-center justify-between gap-3 flex-wrap shrink-0">
          <p className="text-xs text-muted-foreground first-letter:uppercase min-w-0 truncate">
            {rodapeResumo}
          </p>
          <div className="flex gap-2 shrink-0">
            <Button variant="outline" size="sm" onClick={onClose}>
              <X className="h-4 w-4 mr-1.5" />
              Cancelar
            </Button>
            <Button size="sm" disabled={salvando} onClick={() => salvar(false)}>
              {salvando ? "Salvando…" : isEdit ? "Salvar" : "Marcar reunião"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function proximaHoraCheia(): Date {
  const d = new Date();
  d.setMinutes(0, 0, 0);
  d.setHours(d.getHours() + 1);
  return d;
}
